import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';

const API_BASE_URL = (
  (import.meta.env.VITE_API_URL as string) ?? ''
).replace(/\/$/, '');

/**
 * Coarse SSE event names from the artifact-level stream. Per the GDN-938
 * handoff: these are artifact-lifecycle signals, NOT per-DocRef. Each one
 * may trigger a refetch of derived caches (timeline, DocRef list, document
 * list) since that's where the user-visible state lives.
 */
export const ARTIFACT_EVENT_NAMES = [
  'artifact.ingested',
  'artifact.decomposed',
  'artifact.parsing',
  'artifact.parsed',
  'artifact.processed',
  'artifact.failed',
  'artifact.updated',
  'intake_hub.extraction_complete',
] as const;

export type ArtifactEventName = (typeof ARTIFACT_EVENT_NAMES)[number];

/**
 * Domain-side-effect event name (GDN-1032 follow-up). Fires after a
 * DocRef has been created AND a registered extractor has run against
 * it (e.g. the insurance-card vision extractor). Carries the extracted
 * summary so the FE can render a "review & save" affordance without
 * waiting for the next DocRef detail fetch.
 */
export const DOCREF_EXTRACTION_EVENT = 'document_reference.extraction_ready' as const;

/**
 * Lifecycle events that materially change what derived views (timeline,
 * documents list) should show. Pre-decomposition events (`ingested`,
 * `parsing`) don't shift the list — the artifact already exists; clinical
 * fields haven't landed yet.
 */
export const ARTIFACT_INVALIDATING_EVENTS = new Set<ArtifactEventName>([
  'artifact.decomposed',
  'artifact.parsed',
  'artifact.processed',
  'artifact.failed',
  'artifact.updated',
  'intake_hub.extraction_complete',
]);

/**
 * Minimal DocRef head attached to enriched artifact lifecycle events so the
 * FE can swap the ghost card to the extracted title in-place, without
 * waiting for the list refetch to arrive.
 */
export interface ArtifactEventDocumentReference {
  id: string;
  title: string;
  docKind: string | null;
}

/**
 * Parsed payload from an artifact SSE event. The BE sends a full
 * artifact summary; we narrow to the fields the FE actually consumes.
 */
export interface ArtifactEvent {
  name: ArtifactEventName;
  artifactId: string;
  /** processing_status from the BE (`uploaded` / `parsing` / `parsed` /
   *  `decomposed` / `processed` / `failed`). Drives the ghost caption. */
  status: string;
  /** Present on enriched lifecycle events. Empty array means Fast Card
   *  ran but produced no clinical records (e.g. an admin contract). */
  documentReferences?: ArtifactEventDocumentReference[];
}

/**
 * Insurance-card extraction summary as returned by the BE on
 * `document_reference.extraction_ready`. Loosely typed (BE schema
 * doesn't model the per-kind shape today) so we narrow at the call
 * site as needed.
 */
export interface DocRefExtractionEvent {
  documentReferenceId: string;
  artifactId: string;
  /** Which extractor ran — e.g. `"insurance_card"`. */
  kind: string;
  /** Extracted fields. Shape varies per kind — read with care. */
  summary: Record<string, unknown>;
}

interface SubscribeOptions {
  /** Aborted on cleanup to tear down the SSE connection. */
  controller: AbortController;
  /** Fires for each invalidating artifact event — drives RTK cache
   *  refetches for the timeline / docs list. */
  onInvalidate: (event: ArtifactEventName) => void;
  /** Fires for every artifact event with the parsed payload. Used by
   *  the optimistic-upload state to advance ghost captions through the
   *  pipeline and dismiss them on terminal events. */
  onEvent?: (event: ArtifactEvent) => void;
  /** Fires when a domain extractor has produced a result against a
   *  freshly-created DocRef. Use to invalidate the DocRef detail and
   *  surface the "review extraction" affordance. */
  onDocRefExtraction?: (event: DocRefExtractionEvent) => void;
}

interface ArtifactEventPayload {
  artifact_id?: string;
  processing_status?: string;
  /** Set on enriched lifecycle events after Fast Card creates DocRefs. */
  document_references?: Array<{
    document_reference_id?: string;
    title?: string;
    doc_kind?: string | null;
  }>;
}

interface DocRefExtractionPayload {
  document_reference_id?: string;
  artifact_id?: string;
  kind?: string;
  summary?: Record<string, unknown>;
}

function parseDocRefExtractionEvent(data: string): DocRefExtractionEvent | null {
  try {
    const payload = JSON.parse(data) as DocRefExtractionPayload;
    if (!payload.document_reference_id || !payload.artifact_id || !payload.kind) {
      return null;
    }
    return {
      documentReferenceId: payload.document_reference_id,
      artifactId: payload.artifact_id,
      kind: payload.kind,
      summary: payload.summary ?? {},
    };
  } catch {
    return null;
  }
}

function parseArtifactEvent(
  name: ArtifactEventName,
  data: string,
): ArtifactEvent | null {
  try {
    const payload = JSON.parse(data) as ArtifactEventPayload;
    if (!payload.artifact_id || !payload.processing_status) return null;
    return {
      name,
      artifactId: payload.artifact_id,
      status: payload.processing_status,
      documentReferences: payload.document_references
        ?.filter(d => d.document_reference_id && d.title)
        .map(d => ({
          id: d.document_reference_id ?? '',
          title: d.title ?? '',
          docKind: d.doc_kind ?? null,
        })),
    };
  } catch {
    return null;
  }
}

/**
 * Subscribe to the patient-scoped artifact event stream. Resolves once the
 * stream ends (typically when the controller aborts).
 */
export async function subscribeToPatientArtifactEvents(
  patientId: string,
  { controller, onInvalidate, onEvent, onDocRefExtraction }: SubscribeOptions,
): Promise<void> {
  let token: string;
  try {
    token = await getAuth0AccessTokenSilently();
  } catch {
    return;
  }

  await fetchEventSource(
    `${API_BASE_URL}/v2/provider/patients/${patientId}/artifacts/events`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      async onopen(res) {
        if (res.ok) return;
        if (res.status === 401) {
          try {
            token = await getAuth0AccessTokenSilently({ cacheMode: 'off' });
          } catch {
            controller.abort();
          }
          return;
        }
        throw new Error(`SSE failed: ${res.status}`);
      },
      onmessage(ev) {
        if (!ev.event) return;
        // DocRef-level extraction-ready event has its own shape — handle
        // separately from the artifact-lifecycle stream.
        if (ev.event === DOCREF_EXTRACTION_EVENT) {
          if (!onDocRefExtraction) return;
          const parsed = parseDocRefExtractionEvent(ev.data);
          if (parsed) onDocRefExtraction(parsed);
          return;
        }
        if (!ARTIFACT_EVENT_NAMES.includes(ev.event as ArtifactEventName)) {
          return;
        }
        const name = ev.event as ArtifactEventName;
        if (onEvent) {
          const parsed = parseArtifactEvent(name, ev.data);
          if (parsed) onEvent(parsed);
        }
        if (ARTIFACT_INVALIDATING_EVENTS.has(name)) onInvalidate(name);
      },
      onerror() {},
    },
  ).catch(() => {});
}

/** Sibling for the org-scoped event stream — same shape, different URL. */
export async function subscribeToOrganizationArtifactEvents(
  organizationId: string,
  { controller, onInvalidate, onEvent, onDocRefExtraction }: SubscribeOptions,
): Promise<void> {
  let token: string;
  try {
    token = await getAuth0AccessTokenSilently();
  } catch {
    return;
  }

  await fetchEventSource(
    `${API_BASE_URL}/v2/provider/organizations/${organizationId}/artifacts/events`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      async onopen(res) {
        if (res.ok) return;
        if (res.status === 401) {
          try {
            token = await getAuth0AccessTokenSilently({ cacheMode: 'off' });
          } catch {
            controller.abort();
          }
          return;
        }
        throw new Error(`SSE failed: ${res.status}`);
      },
      onmessage(ev) {
        if (!ev.event) return;
        // DocRef-level extraction-ready event has its own shape — handle
        // separately from the artifact-lifecycle stream.
        if (ev.event === DOCREF_EXTRACTION_EVENT) {
          if (!onDocRefExtraction) return;
          const parsed = parseDocRefExtractionEvent(ev.data);
          if (parsed) onDocRefExtraction(parsed);
          return;
        }
        if (!ARTIFACT_EVENT_NAMES.includes(ev.event as ArtifactEventName)) {
          return;
        }
        const name = ev.event as ArtifactEventName;
        if (onEvent) {
          const parsed = parseArtifactEvent(name, ev.data);
          if (parsed) onEvent(parsed);
        }
        if (ARTIFACT_INVALIDATING_EVENTS.has(name)) onInvalidate(name);
      },
    },
  ).catch(() => {});
}

/** Admin org-scoped event stream. Same payload shape, admin auth surface. */
export async function subscribeToAdminOrganizationArtifactEvents(
  organizationId: string,
  { controller, onInvalidate, onEvent, onDocRefExtraction }: SubscribeOptions,
): Promise<void> {
  let token: string;
  try {
    token = await getAuth0AccessTokenSilently();
  } catch {
    return;
  }

  await fetchEventSource(
    `${API_BASE_URL}/v2/admin/organizations/${organizationId}/artifacts/events`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      async onopen(res) {
        if (res.ok) return;
        if (res.status === 401) {
          try {
            token = await getAuth0AccessTokenSilently({ cacheMode: 'off' });
          } catch {
            controller.abort();
          }
          return;
        }
        throw new Error(`SSE failed: ${res.status}`);
      },
      onmessage(ev) {
        if (!ev.event) return;
        if (ev.event === DOCREF_EXTRACTION_EVENT) {
          if (!onDocRefExtraction) return;
          const parsed = parseDocRefExtractionEvent(ev.data);
          if (parsed) onDocRefExtraction(parsed);
          return;
        }
        if (!ARTIFACT_EVENT_NAMES.includes(ev.event as ArtifactEventName)) {
          return;
        }
        const name = ev.event as ArtifactEventName;
        if (onEvent) {
          const parsed = parseArtifactEvent(name, ev.data);
          if (parsed) onEvent(parsed);
        }
        if (ARTIFACT_INVALIDATING_EVENTS.has(name)) onInvalidate(name);
      },
    },
  ).catch(() => {});
}
